import { GridColDef } from "@mui/x-data-grid-premium";
import React from "react";

import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { Latest, MixGrouping, Next } from "src/store/api/generatedApi";

import { useTenantData } from "contexts/tenant";
import { Period } from "hooks/periodIndex";
import { useMixGrouping } from "hooks/useMixGrouping";
import { useMixQuantity } from "hooks/useMixQuantity";
import { MixQuantity } from "src/store/api/generatedApi";
import { useFormatNumber } from "src/utils/formatNumber";
import { PlanId } from "../boundary/PlanId";
import {
  basketWeight,
  copperGroup,
  driWeight,
  grabType,
  LayerEditingHooks,
  material as materialColumn,
  mixDiff,
  subStandardGroup,
  supervisorSteelGrades,
} from "./columns";
import { useFilterModel } from "./filterModel";
import { useSx } from "./sx";
import { Table } from "./Table";
import { Material, Row } from "./types";

interface Props {
  planId: PlanId;
  disableEdit: boolean;
  rows: Row[];
  materials: Material[];
  deploymentType: "standard" | "edited_standard" | "backup" | "edited_backup";
  originalDeploymentMixIds: Record<number, number>;
  planTimeframe: Latest | Next;
  startAt: Date;
  endAt: Date;
  selectedSteelGradeIds: number[];
  onChange: (rows: Row[]) => void;
  hasDri: boolean;
  layerEditing?: LayerEditingHooks;
}

const useColumns = (
  materials: Material[],
  selectedSteelGradeIds: number[],
  disableEdit: boolean,
  mixGrouping: MixGrouping,
  deploymentType: "standard" | "edited_standard" | "backup" | "edited_backup",
  originalDeploymentMixIds: Record<number, number>,
  planTimeframe: Latest | Next,
  startAt: Date,
  endAt: Date,
  hasDri: boolean,
  layerEditing: LayerEditingHooks | undefined
) => {
  const bracketUnits = useUnitsFormatter(true);
  const { t } = useTenantTranslation();
  const { default_mix_breakdown, small_mass_per_mass, rounding_units } =
    useTenantData();
  const massUnit =
    default_mix_breakdown === "small_mass" ? "small_mass" : "mass";
  const massScaleFactor =
    default_mix_breakdown === "small_mass" ? small_mass_per_mass : 1;
  const materialFormat = useFormatNumber()(3, false);
  const totalFormat = useFormatNumber()(0, true);
  const mixQuantity = useMixQuantity();
  const groupingColumn = React.useMemo(() => {
    switch (mixGrouping) {
      case "copper_group":
        return copperGroup;
      case "grouping_name":
        return subStandardGroup(t);
    }
  }, [mixGrouping, t]);

  const columns = React.useMemo(() => {
    return [
      mixDiff(
        deploymentType,
        originalDeploymentMixIds,
        planTimeframe,
        startAt,
        endAt
      ),
      supervisorSteelGrades(t, 350, selectedSteelGradeIds),
      groupingColumn,
      ...(hasDri
        ? [driWeight(t, massUnit, massScaleFactor, bracketUnits)]
        : []),
      basketWeight(
        t,
        massUnit,
        massScaleFactor,
        totalFormat,
        bracketUnits,
        disableEdit,
        massUnit,
        layerEditing
      ),
      grabType(t),
      ...materials.map((material): GridColDef<Row> =>
        materialColumn(
          material,
          massScaleFactor,
          disableEdit,
          materialFormat,
          rounding_units
        )
      ),
    ];
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- bracketUnits, groupingColumn, hasDri, massScaleFactor, massUnit, materialFormat, t, totalFormat are formatter/hook values tracked via mixQuantity
  }, [
    materials,
    selectedSteelGradeIds,
    originalDeploymentMixIds,
    disableEdit,
    deploymentType,
    planTimeframe,
    startAt,
    endAt,
    mixQuantity,
    layerEditing,
  ]);
  return columns;
};

const useColumnVisibilityModel = (mixQuantity: MixQuantity) => {
  const columnVisibilityModel = React.useMemo(() => {
    return mixQuantity === "grabs"
      ? {
          mix_name: false,
          basket_weight: false,
          steel_grades: false,
          grabType: true,
        }
      : {
          mix_name: true,
          basket_weight: true,
          steel_grades: true,
          grabType: false,
        };
  }, [mixQuantity]);
  return columnVisibilityModel;
};

export const SupervisorTable = ({
  disableEdit,
  materials,
  selectedSteelGradeIds,
  deploymentType,
  planTimeframe,
  startAt,
  endAt,
  rows,
  originalDeploymentMixIds,
  onChange,
  planId,
  hasDri,
  layerEditing,
}: Props) => {
  const mixGrouping = useMixGrouping();
  const mixQuantity = useMixQuantity();
  const columns = useColumns(
    materials,
    selectedSteelGradeIds,
    disableEdit,
    mixGrouping,
    deploymentType,
    originalDeploymentMixIds,
    planTimeframe,
    startAt,
    endAt,
    hasDri,
    layerEditing
  );
  const pinnedColumns = React.useMemo(() => {
    return columns.slice(0, hasDri ? 6 : 5).map((column) => column.field);
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- hasDri is captured via columns which already depends on it
  }, [columns]);
  const filterModel = useFilterModel(planId, 1 as Period, "supervisor");
  const sx = useSx();
  const columnVisibilityModel = useColumnVisibilityModel(mixQuantity);

  return (
    <Table
      rows={rows}
      onChange={onChange}
      filterModel={filterModel}
      columnGroupingModel={[]}
      columns={columns}
      pinnedColumns={pinnedColumns}
      disableEdit={disableEdit}
      sx={sx}
      columnVisibilityModel={columnVisibilityModel}
    />
  );
};
